import { FormEvent, useEffect, useState, type CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'
import { sellerVerificationAPI } from '../api/client'

export type SellerVerificationState = {
  level: number
  can_sell: boolean
  can_high_value: boolean
  high_value_threshold_eur: number
  seller_type: 'individual' | 'company' | null
  status: 'none' | 'pending' | 'approved' | 'rejected'
  contact_phone?: string | null
  company_name?: string | null
  company_registration_code?: string | null
  note?: string | null
  provider?: string
}

type Props = {
  token: string
  defaultFullName?: string
  onStatusChange?: (status: SellerVerificationState) => void
}

function TierBadges({ level, threshold }: { level: number; threshold: number }) {
  const { t } = useTranslation()
  const tiers = [
    { n: 1, label: t('sellerVerify.tier1') },
    { n: 2, label: t('sellerVerify.tier2') },
    { n: 3, label: t('sellerVerify.tier3', { amount: threshold }) },
  ]
  const fillPct = tiers.length <= 1 ? 0 : (Math.max(0, level - 1) / (tiers.length - 1)) * 100

  return (
    <div className="seller-verify-stepper" role="list" aria-label={t('sellerVerify.tiersLegend')}>
      <div className="seller-verify-stepper__rail" aria-hidden="true">
        <div className="seller-verify-stepper__fill" style={{ width: `${fillPct}%` }} />
      </div>
      <div className="seller-verify-stepper__steps">
        {tiers.map((tier, i) => (
          <div
            key={tier.n}
            role="listitem"
            className={`seller-verify-stepper__step${
              level >= tier.n ? ' is-done' : ''
            }${level + 1 === tier.n ? ' is-next' : ''}`}
            style={{ '--step-i': i } as CSSProperties}
          >
            <span className="seller-verify-stepper__dot" aria-hidden="true">
              {level >= tier.n ? '✓' : tier.n}
            </span>
            <span className="seller-verify-stepper__label">{tier.label}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

export default function SellerVerificationSection({
  token,
  defaultFullName = '',
  onStatusChange,
}: Props) {
  const { t } = useTranslation()
  const [status, setStatus] = useState<SellerVerificationState | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [sellerType, setSellerType] = useState<'individual' | 'company'>('individual')
  const [fullName, setFullName] = useState(defaultFullName)
  const [phone, setPhone] = useState('')
  const [companyName, setCompanyName] = useState('')
  const [companyCode, setCompanyCode] = useState('')

  function load() {
    setLoading(true)
    sellerVerificationAPI
      .getStatus(token)
      .then((res) => {
        const data = res.data as SellerVerificationState
        setStatus(data)
        onStatusChange?.(data)
        if (data.seller_type) setSellerType(data.seller_type)
        if (data.contact_phone) setPhone(data.contact_phone)
        if (data.company_name) setCompanyName(data.company_name)
        if (data.company_registration_code) setCompanyCode(data.company_registration_code)
      })
      .catch(() => setStatus(null))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  useEffect(() => {
    if (defaultFullName && !fullName) setFullName(defaultFullName)
  }, [defaultFullName, fullName])

  function profileBody() {
    return {
      seller_type: sellerType,
      full_name: sellerType === 'individual' ? fullName.trim() : undefined,
      contact_phone: phone.trim() || undefined,
      company_name: sellerType === 'company' ? companyName.trim() : undefined,
      company_registration_code: sellerType === 'company' ? companyCode.trim() : undefined,
    }
  }

  async function onSaveProfile(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    try {
      const res = await sellerVerificationAPI.saveProfile(token, profileBody())
      const data = res.data as SellerVerificationState
      setStatus(data)
      onStatusChange?.(data)
    } catch (err: unknown) {
      const detail =
        err &&
        typeof err === 'object' &&
        'response' in err &&
        (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
      alert(typeof detail === 'string' ? detail : t('sellerVerify.submitFailed'))
    } finally {
      setBusy(false)
    }
  }

  async function onApplyL3(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    try {
      const res = await sellerVerificationAPI.apply(token, profileBody())
      const data = res.data as SellerVerificationState
      setStatus(data)
      onStatusChange?.(data)
    } catch (err: unknown) {
      const detail =
        err &&
        typeof err === 'object' &&
        'response' in err &&
        (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
      alert(typeof detail === 'string' ? detail : t('sellerVerify.submitFailed'))
    } finally {
      setBusy(false)
    }
  }

  if (loading) {
    return <p className="meta-text seller-verify-loading">{t('common.loading')}</p>
  }

  if (!status) {
    return (
      <div className="seller-verify seller-verify--error">
        <p className="meta-text seller-verify-error">{t('sellerVerify.loadError')}</p>
        <button type="button" className="seller-verify-submit" onClick={load} disabled={loading}>
          {t('sellerVerify.retry')}
        </button>
      </div>
    )
  }

  const threshold = status.high_value_threshold_eur ?? 1000
  const showProfileForm = !status.can_sell
  const showL3Upgrade = status.can_sell && !status.can_high_value && status.status !== 'pending'
  const showL3Pending = status.status === 'pending'
  const showL3Approved = status.can_high_value

  function profileFields() {
    return (
      <>
        <div className="seller-verify-type" role="radiogroup" aria-label={t('sellerVerify.typeLegend')}>
          <label className="seller-verify-type-option">
            <input
              type="radio"
              name="seller_type"
              checked={sellerType === 'individual'}
              onChange={() => setSellerType('individual')}
            />
            <span>{t('sellerVerify.typeIndividual')}</span>
          </label>
          <label className="seller-verify-type-option">
            <input
              type="radio"
              name="seller_type"
              checked={sellerType === 'company'}
              onChange={() => setSellerType('company')}
            />
            <span>{t('sellerVerify.typeCompany')}</span>
          </label>
        </div>
        {sellerType === 'individual' ? (
          <label>
            {t('sellerVerify.fullName')}
            <input value={fullName} onChange={(e) => setFullName(e.target.value)} required maxLength={200} />
          </label>
        ) : (
          <>
            <label>
              {t('sellerVerify.companyName')}
              <input value={companyName} onChange={(e) => setCompanyName(e.target.value)} required maxLength={200} />
            </label>
            <label>
              {t('sellerVerify.companyCode')}
              <input
                value={companyCode}
                onChange={(e) => setCompanyCode(e.target.value)}
                required
                maxLength={32}
                placeholder="123456789"
              />
            </label>
          </>
        )}
        <label>
          {t('sellerVerify.phone')}
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            maxLength={32}
            placeholder="+370..."
          />
        </label>
      </>
    )
  }

  return (
    <div className="seller-verify" id="seller-verification">
      <TierBadges level={status.level} threshold={threshold} />
      <p className="seller-verify-intro">{t('sellerVerify.intro')}</p>

      {status.status === 'rejected' && (
        <p className="error-text seller-verify-rejected" role="alert">
          {status.note ? t('sellerVerify.rejectedWithNote', { note: status.note }) : t('sellerVerify.rejected')}
        </p>
      )}

      {showL3Approved ? (
        <div className="seller-verify seller-verify--approved">
          <p className="success-text seller-verify-badge">{t('sellerVerify.approvedL3Title')}</p>
          <p className="meta-text">
            {status.seller_type === 'company'
              ? t('sellerVerify.approvedCompany', { name: status.company_name ?? '' })
              : t('sellerVerify.approvedIndividual')}
          </p>
        </div>
      ) : null}

      {showL3Pending ? (
        <div className="seller-verify seller-verify--pending">
          <p className="seller-verify-badge seller-verify-badge--pending">{t('sellerVerify.pendingL3Title')}</p>
          <p className="meta-text">{t('sellerVerify.pendingL3Hint', { amount: threshold })}</p>
        </div>
      ) : null}

      {status.can_sell && !showL3Approved && !showL3Pending ? (
        <div className="seller-verify seller-verify--approved">
          <p className="success-text seller-verify-badge">{t('sellerVerify.approvedL2Title')}</p>
          <p className="meta-text">{t('sellerVerify.approvedL2Hint', { amount: threshold })}</p>
        </div>
      ) : null}

      {showProfileForm ? (
        <form className="seller-verify-form" onSubmit={onSaveProfile}>
          {profileFields()}
          <p className="meta-text seller-verify-legal">{t('sellerVerify.profileLegalHint')}</p>
          <button type="submit" className="seller-verify-submit" disabled={busy}>
            {busy ? '…' : t('sellerVerify.saveProfile')}
          </button>
        </form>
      ) : null}

      {showL3Upgrade ? (
        <form className="seller-verify-form seller-verify-form--l3" onSubmit={onApplyL3}>
          <p className="meta-text">{t('sellerVerify.l3Intro', { amount: threshold })}</p>
          {profileFields()}
          <p className="meta-text seller-verify-legal">{t('sellerVerify.legalHint')}</p>
          <button type="submit" className="seller-verify-submit" disabled={busy}>
            {busy ? '…' : t('sellerVerify.applyL3')}
          </button>
        </form>
      ) : null}
    </div>
  )
}
